<template>

  <div class="col-md-12">

    <div class="card">
      <div v-if="!readonly" class="card-toolbar">
        <div class="main-actions">
          <button v-if="!readonly" class="main-btn main-btn-success" data-bs-toggle="modal"
                  title="Nova Prescrição" :data-bs-target="'#modal_store_prescription_' + modalRecordId"
                  @click="resetCreateForm">
            <i class="bi bi-plus-lg"></i>
            <span>Nova Prescrição</span>
          </button>
          <button
            v-if="!readonly && showPrescriptionAiShortcuts"
            type="button"
            class="main-btn main-btn-secondary"
            @click="openAiAssistant('prescription')"
          >
            <i class="bi bi-stars"></i>
            <span>IA</span>
          </button>
        </div>
      </div>

      <div v-if="prescriptions.length === 0" class="empty-state">
        <div class="empty-state-content">
          <div class="empty-state-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none">
              <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#780f5a"/>
              <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="#780f5a"/>
              <path d="M12 14H8V16H12V14ZM16 10H8V12H16V10ZM8 6H16V8H8V6Z" fill="#780f5a"/>
            </svg>
          </div>
          <h3 class="empty-state-title">Nenhuma prescrição</h3>
          <p class="empty-state-description" v-if="!readonly">
            Ainda não foi adicionada nenhuma prescrição para este atendimento.<br>
            Clique no botão "Nova Prescrição" acima para começar.
          </p>
        </div>
      </div>

      <div class="prescription-card" v-for="prescription in prescriptions" :key="prescription.id_presc">
        <div class="prescription-header collapsible cursor-pointer rotate"
             data-bs-toggle="collapse" :data-bs-target="'#kt_prescription_card_collapsible_' + documentDomId(prescription.id_presc)">
          <div class="prescription-title">
            <div class="prescription-icon">
              <span class="svg-icon svg-icon-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor"/>
                  <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                </svg>
              </span>
            </div>
            <div class="prescription-info">
              <h3 class="prescription-name">{{ prescriptionTitle(prescription) }}</h3>
              <span v-if="prescription.id_task_origin" class="badge bg-light-primary text-primary">Emissão avulsa · Tarefa #{{ prescription.id_task_origin }}</span>
              <div class="prescription-status">
                <span :title="prescription.presc_request_generated?'Alteração bloqueada - PDF já gerado':'Liberado para alteração'"
                      :class="prescription.presc_request_generated?'status-icon locked':'status-icon unlocked'">
                  <i :class="prescription.presc_request_generated?'bi bi-lock-fill':'bi bi-unlock-fill'"></i>
                </span>
                <span v-if="prescription.presc_request_generated" title="PDF gerado" class="status-icon pdf">
                  <i class="bi bi-file-pdf-fill"></i>
                </span>
                <span v-if="prescription.presc_request_signed" title="Assinado" class="status-icon signed">
                  <i class="bi bi-pen-fill"></i>
                </span>
              </div>
            </div>
          </div>
          <div class="prescription-toolbar rotate-180">
            <span class="svg-icon svg-icon-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
              </svg>
            </span>
          </div>
        </div>

        <div :id="'kt_prescription_card_collapsible_' + documentDomId(prescription.id_presc)" class="collapse show">
          <div class="prescription-actions">
            <div class="action-buttons">
              <button v-if="!readonly && prescription.presc_request_generated" class="action-btn action-btn-warning action-btn-reopen"
                      title="Reabrir para Alteração" @click="reopenPrescription(prescription.id_presc)">
                <i class="bi bi-unlock-fill"></i>
                <span>Reabrir</span>
              </button>
              <button v-if="!readonly && !prescription.presc_request_generated && canImportFromRecord(prescription)"
                      class="action-btn action-btn-secondary"
                      title="Importar do prontuário" @click="importFromRecord(prescription)">
                <i class="bi bi-download"></i>
                <span>Importar</span>
              </button>
              <button class="action-btn action-btn-info action-btn-print"
                      :disabled="isPrintBlocked(prescription)"
                      :title="isPrintBlocked(prescription) ? printBlockMessage(prescription) : 'Imprimir'"
                      @click="startPrint(prescription)">
                <i class="bi bi-printer"></i>
                <span>{{ taskContext ? 'Emitir / assinar' : 'Imprimir' }}</span>
              </button>
              <button v-if="!readonly && isMedicationPrescription(prescription)"
                      class="action-btn action-btn-secondary" data-bs-toggle="modal"
                      :data-bs-target="'#modal_save_prescription_model_' + modalRecordId"
                      title="Salvar esta prescrição como modelo"
                      @click="openSaveAsModel(prescription)">
                <i class="bi bi-bookmark-plus"></i>
                <span>Salvar</span>
              </button>
              <button v-if="!readonly" class="action-btn action-btn-danger action-btn-delete"
                      title="Excluir Receita" @click="deletePrescription(prescription.id_presc)">
                <i class="bi bi-trash"></i>
                <span>Excluir</span>
              </button>
            </div>
          </div>
          <div class="card-body">
            <PrescriptionContentEditor
                :key="'presc-editor-' + documentDomId(prescription.id_presc) + '-' + JSON.stringify(prescription.presc_payload)"
                :prescription="prescription"
                :medical-record="medicalRecord"
                :drug-admins="drugAdmins"
                :prescription-protocols="prescriptionProtocols"
                :readonly="readonly || prescription.presc_request_generated"
                :regulatory-plan="regulatoryPlans[prescription.id_presc]"
                :regulatory-types="regulatoryTypes"
                :presc-types="prescTypes"
                @update:payload="savePrescriptionPayload(prescription, $event)"
                @regulatory-changed="fetchRegulatoryPlan(prescription)"
            />
            <div class="col-md-12 mt-4">
              <label class="form-label">Observação geral</label>
              <textarea class="form-control form-control-solid"
                        rows="3"
                        placeholder="Ex.: Evitar exposição ao sol durante o tratamento"
                        :disabled="readonly || prescription.presc_request_generated"
                        :value="prescription.presc_obs"
                        @change="savePrescriptionObs(prescription, $event.target.value)"></textarea>
            </div>
          </div>
        </div>

        <Teleport to="body">
<div class="modal fade" data-bs-backdrop="static" data-bs-keyboard="false"
             :id="'modal_print_prescription_'  + documentDomId(prescription.id_presc)" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header" v-if="messagesGeneratePrescription.showBtnClose">
                <div class="btn btn-icon btn-sm btn-active-light-primary ms-2"
                     data-bs-dismiss="modal" aria-label="Fechar">
                  <i class="bi bi-x-lg"></i>
                </div>
              </div>
              <div class="modal-body">
                <div class="col-md-12 mb-6 mt-4">
                  <div class="text-center px-5" :key="messagesGeneratePrescription.image">
                    <video width="200" height="200" :loop="messagesGeneratePrescription.loop" autoplay playsinline muted v-if="messagesGeneratePrescription.image.includes('.mp4')">
                      <source :src="messagesGeneratePrescription.image" type="video/mp4" />
                    </video>
                    <img v-else-if="messagesGeneratePrescription.image" :src="messagesGeneratePrescription.image" alt="Imagem" class="mw-100 h-200px h-sm-325px">
                    <p v-html="messagesGeneratePrescription.message"></p>
                  </div>
                </div>
              </div>
              <div class="modal-footer" v-if="messagesGeneratePrescription.showBtnClose">
                <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
                <button v-if="messagesGeneratePrescription.actionLabel" type="button" class="btn btn-primary"
                        @click="messagesGeneratePrescription.action()">
                  {{ messagesGeneratePrescription.actionLabel }}
                </button>
              </div>
            </div>
          </div>
        </div>
  </Teleport>

      </div>
    </div>
  </div>

  <Teleport to="body">
    <div ref="createModal" class="modal fade prescription-create" tabindex="-1" data-bs-focus="false" data-bs-backdrop="false"
         @click.self="closeCreateModal"
         :aria-labelledby="'prescription_create_title_' + modalRecordId" :id="'modal_store_prescription_' + modalRecordId">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <form method="post" @submit.prevent="submitFormStore">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" :id="'prescription_create_title_' + modalRecordId">Nova Prescrição</h5>
              <button type="button" class="prescription-create__chip prescription-create__close" data-bs-dismiss="modal" aria-label="Fechar">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div class="modal-body">
            <p v-if="taskContext" class="alert alert-primary py-3"><strong>{{ taskContext.patientName }}</strong> · Emissão avulsa vinculada ao atendimento de {{ taskContext.recordDate }}.</p>
              <div class="d-flex flex-wrap gap-2 mb-4">
                <button type="button" class="prescription-create__chip" :class="{ 'is-active': !fromPrevious }" :aria-pressed="!fromPrevious" @click="fromPrevious = false">Em branco ou modelo</button>
                <button type="button" class="prescription-create__chip" :class="{ 'is-active': fromPrevious }" :aria-pressed="fromPrevious" @click="fromPrevious = true">A partir de consulta anterior</button>
              </div>
              <PreviousDocumentPicker v-if="fromPrevious" agenda-style :professional-id="schedule.id_prof" :patient-id="schedule.id_patient"
                :record-id="medicalRecord.id_medrec" :task-context="taskContext" kind="prescriptions" @created="onPreviousCopied" @empty="fromPrevious = false" />
              <template v-else>
                <div class="col-md-12 mb-4">
                  <label :for="'prot_store_' + modalRecordId" class="form-label">Modelo de prescrição</label>
                  <SelectUbbi :id="'prot_store_' + modalRecordId"
                              v-model="formStore.id_presc_prot"
                              :options="[{ id_presc_prot: '', presc_prot_name: 'Em branco' }, ...(prescriptionProtocols || [])]"
                              option-label="presc_prot_name" option-value="id_presc_prot"
                              placeholder="Em branco" searchable @change="onModelChange" />
                </div>
                <div v-if="isMedicationCreate" class="prescription-create__divider">
                  <span>ou adicione os medicamentos agora</span>
                </div>
                <p v-if="isMedicationCreate" class="prescription-create__divider-hint text-center">
                  Sem modelo, monte a prescrição aqui mesmo.
                </p>
                <DraftDrugComposer v-if="isMedicationCreate"
                                   ref="drugComposer"
                                   v-model="draftDrugs"
                                   :drug-admins="drugAdmins"
                                   :regulatory-types="regulatoryTypes"
                                   id-prefix="create-presc-drug"/>
              </template>
            </div>
            <div class="modal-footer">
              <button type="button" class="prescription-create__chip" data-bs-dismiss="modal">Fechar</button>
              <button v-if="!fromPrevious" type="submit" class="prescription-create__chip prescription-create__chip--primary" :disabled="creating">
                {{ creating ? 'Cadastrando...' : 'Cadastrar' }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </Teleport>

  <PdfViewer :id="'modal_pdf_viewer_prescription_' + this.modalRecordId"
             :pdf-source="this.pdfSource"
             :file-name="this.fileName"
             :signed="this.signed"
             :delivery="documentsDelivery"
             :key="'Prescription:'+ modalRecordId"
             @edit-contact="openPatientContact"/>

  <SelectCertifier :id="'modal_certifier_prescription_' + this.modalRecordId"
                   :certifiers="this.certifiers"
                   :key="'PrescCertifier:'+ modalRecordId"
                   @onSelect="printPrescription"/>

  <PrescriptionRegulatoryPlanModal :id="'modal_regulatory_plan_' + this.modalRecordId"
                                   :plan="planModalPlan"
                                   :certifiers="this.certifiers"
                                   :generating="generating"
                                   :professional-id="sncrProfessionalId"
                                   :return-to="sncrReturnTo"
                                   :key="'PrescPlan:'+ modalRecordId"
                                   @generate="onPlanGenerate"/>

  <PrescriptionDocumentsModal :id="'modal_prescription_documents_' + this.modalRecordId"
                              :documents="generatedDocuments"
                              :blocked="blockedDocuments"
                              :professional-id="sncrProfessionalId"
                              :return-to="sncrReturnTo"
                              :key="'PrescDocs:'+ modalRecordId"
                              @open="openGeneratedDocument"/>


  <SaveAsModelModal :id-modal="'modal_save_prescription_model_' + modalRecordId"
                    title="Salvar prescrição como modelo"
                    hint="O modelo fica disponível na criação de novas prescrições, já com os medicamentos."
                    :summary="saveAsModelSummary"
                    :suggestion="saveAsModelSuggestion"
                    :saving="savingAsModel"
                    @save="savePrescriptionAsModel"/>

</template>

<script>
import { taskDocumentParams, taskDocumentLinked } from '@/views/tasks/taskDocumentContext';
import SelectUbbi from "@/components/SelectUbbi.vue";
import PreviousDocumentPicker from '../shared/PreviousDocumentPicker.vue';
import http from "../../../helpers/http";
import Alert from "../../../helpers/alert";
import OS from "../../../helpers/os";
import Base64 from "../../../helpers/base64";
import PrescriptionDrugs from "./PrescriptionDrugs";
import PrescriptionContentEditor from "../shared/PrescriptionContentEditor";
import DraftDrugComposer from "../shared/DraftDrugComposer.vue";
import PdfViewer from "../shared/PdfViewer";
import Config from "../../../config";
import SelectCertifier from "../../../components/SelectCertificator";
import PrescriptionRegulatoryPlanModal from "../shared/PrescriptionRegulatoryPlanModal";
import PrescriptionDocumentsModal from "../shared/PrescriptionDocumentsModal";
import documentsDelivery from "../shared/documentsDelivery";
import {confirmarAssinaturaSemSelo} from "../shared/signatureSealWarning";
import SaveAsModelModal from "../../../components/SaveAsModelModal";
import { AI_ASSISTANT_PREFS_CHANGED, loadAiAssistantPrefs, resolveEffectiveAiFeatures } from '@/helpers/aiAssistantPrefs';
import { loadRegulatoryTypes, regulatoryTypeName, requiresSncrNumber } from '@/helpers/regulatoryTypes';
import { sncrError, startGovBrAuth } from '@/helpers/sncr';
import { visibleAttributes } from '../shared/record-attributes';

export default {
  name: "Prescription",
  emits: ['signed', 'open-ai-assistant', 'onModelSaved', 'edit-patient-contact'],
  mixins: [documentsDelivery],
  components: {SelectUbbi,
    PreviousDocumentPicker,
    SelectCertifier,
    PrescriptionDrugs,
    PrescriptionContentEditor,
    DraftDrugComposer,
    PrescriptionRegulatoryPlanModal,
    PrescriptionDocumentsModal,
    SaveAsModelModal,
    PdfViewer
  },
  props: {
    taskContext: { type: Object, default: null },
    schedule: Object,
    medicalRecord: Object,
    readonly: Boolean,
    prescriptionProtocols: Array,
    prescTypes: Array,
    drugAdmins: Array,
    certifiers: Array,
    recordTypes: {
      type: Array,
      default: () => [],
    },
    professionalId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      fromPrevious: false,
      prescriptions: [],
      // Para onde os documentos vão ao finalizar o atendimento. Vem da API:
      // a regra de qual e-mail vale é dela, não da tela.
      currentPrescId: '',
      creating: false,
      draftDrugs: [],
      formStore: {
        id_presc_prot: '',
        presc_name: '',
        id_medrec: '',
      },
      formStoreErrors: [],
      saveAsModelSource: null,
      saveAsModelSuggestion: '',
      saveAsModelSummary: '',
      savingAsModel: false,
      messagesGeneratePrescription: {
        message: '',
        image: '',
        showBtnClose: false,
      },
      pdfSource: '',
      fileName: '',
      signed: false,
      selectedPrescription: '',
      aiPrefsVersion: 0,
      regulatoryTypes: [],
      regulatoryPlans: {},
      planModalPrescription: null,
      generating: false,
      generatedDocuments: [],
      blockedDocuments: [],
    }
  },
  mounted() {
    this.$refs.createModal.addEventListener('shown.bs.modal', this.focusCreateModal);
    this.prescriptions = this.taskContext ? [] : (this.medicalRecord.prescriptions || []);
    if (this.taskContext) {
      this.getPrescriptions().then(() => {
        if (!this.prescriptions.length && !this.taskContext.quick) this.$nextTick(() => bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_store_prescription_' + this.modalRecordId)).show());
      }).catch(error => Alert.toastError(error.response?.data?.message || 'Não foi possível carregar os documentos.'));
    } else {
      this.getPrescriptions();
    }
    loadRegulatoryTypes().then((types) => {
      this.regulatoryTypes = types;
    }).catch(() => {
      this.regulatoryTypes = [];
    });
    this.onAiPrefsChanged = (event) => {
      if (String(event.detail?.professionalId) === String(this.professionalId)) {
        this.aiPrefsVersion += 1;
      }
    };
    window.addEventListener(AI_ASSISTANT_PREFS_CHANGED, this.onAiPrefsChanged);
  },
  beforeUnmount() {
    this.$refs.createModal.removeEventListener('shown.bs.modal', this.focusCreateModal);
    window.removeEventListener(AI_ASSISTANT_PREFS_CHANGED, this.onAiPrefsChanged);
  },
  watch: {
    'medicalRecord.id_medrec'() {
      this.getPrescriptions();
    },
  },
  computed: {
    modalRecordId() { return this.medicalRecord.id_medrec + (this.taskContext ? '_task_' + this.taskContext.id : ''); },
    userAuth() {
      return this.$store.state.auth.user;
    },
    place() {
      return this.$store.state.auth.place;
    },
    selectedModel() {
      return this.prescriptionProtocols?.find((model) => model.id_presc_prot == this.formStore.id_presc_prot) ?? null;
    },
    planModalPlan() {
      return this.planModalPrescription
          ? this.regulatoryPlans[this.planModalPrescription.id_presc]
          : null;
    },
    sncrProfessionalId() {
      const prescritor = this.schedule?.id_prof;

      return prescritor && String(prescritor) === String(this.userAuth?.id_prof) ? prescritor : null;
    },
    sncrReturnTo() {
      return {
        path: this.$route.fullPath,
        professionalId: this.sncrProfessionalId,
        prescriptionId: this.planModalPrescription?.id_presc ?? null,
      };
    },
    isMedicationCreate() {
      const model = this.selectedModel;
      return !model || (model.content_type ?? 'medication') === 'medication';
    },
    showPrescriptionAiShortcuts() {
      void this.aiPrefsVersion;

      const features = resolveEffectiveAiFeatures(
        this.recordTypes,
        loadAiAssistantPrefs(this.professionalId),
      );

      return features.prescriptionValidation || features.prescriptionChat;
    },
  },
  methods: {
    documentDomId(id) { return id + (this.taskContext ? '_task_' + this.taskContext.id : ''); },
    async onPreviousCopied(result) {
      await this.getPrescriptions();
      this.closeCreateModal();
      this.fromPrevious = false;
      if (result?.certifier) {
        const copy = this.prescriptions.find(item => Number(item.id_presc) === Number(result.document.id_presc));
        if (!copy) return;
        await this.$nextTick();
        await this.startPrint(copy, result.certifier);
      }
    },
    openAiAssistant(section) {
      this.$emit('open-ai-assistant', {
        section,
        medrecId: this.medicalRecord.id_medrec,
      });
    },
    openSaveAsModel(prescription) {
      this.saveAsModelSource = prescription;
      this.saveAsModelSuggestion = prescription.presc_name ?? '';
      this.saveAsModelSummary = '';

      http.get(this.drugsUrl(prescription)).then((response) => {
        const drugs = response.data ?? [];
        this.saveAsModelSummary = drugs.length === 1
            ? '1 medicamento será salvo no modelo.'
            : `${drugs.length} medicamentos serão salvos no modelo.`;

        // Prescrição raramente tem nome próprio: o primeiro medicamento é o
        // que o médico reconhece na lista de modelos depois.
        if (!this.saveAsModelSuggestion && drugs.length > 0) {
          this.saveAsModelSuggestion = drugs[0].drug_name ?? '';
        }
      }).catch(() => {
        this.saveAsModelSummary = '';
      });
    },
    drugsUrl(prescription) {
      return `/professionals/${this.medicalRecord.id_prof}/patients/${this.medicalRecord.id_patient}/medical-records/${prescription.id_medrec}/prescriptions/${prescription.id_presc}/drugs`;
    },
    savePrescriptionAsModel(name) {
      const prescription = this.saveAsModelSource;
      if (!prescription) {
        return;
      }

      this.savingAsModel = true;

      // Os itens vêm da API na hora de salvar: PrescriptionDrugs mantém a
      // própria lista e o pai não acompanha as edições dela.
      http.get(this.drugsUrl(prescription)).then((response) => {
        const drugs = (response.data ?? []).map((drug) => ({
          item_kind: drug.item_kind ?? 'medication',
          drug_name: drug.drug_name,
          id_drug: drug.id_drug ?? null,
          concentration: drug.concentration ?? null,
          pharmaceutical_form: drug.pharmaceutical_form ?? null,
          quantity: drug.quantity ?? null,
          measure: drug.measure ?? null,
          composition: drug.composition ?? null,
          regulatory_type: drug.regulatory_type_manual ?? null,
          continuous_use: drug.continuous_use ?? false,
          dosage_desc: drug.dosage_desc,
          treatment_time: drug.treatment_time ?? null,
          drug_obs: drug.drug_obs ?? null,
          id_drug_admin: drug.id_drug_admin,
          // Sem isto o modelo salva e nasce vazio ao ser usado: importProtocol
          // filtra os itens por id_presc_type.
          id_presc_type: prescription.id_presc_type,
        }));

        if (drugs.length === 0) {
          this.savingAsModel = false;
          Alert.toastError('Adicione ao menos um medicamento antes de salvar como modelo.');

          return;
        }

        return http.post(`/professionals/${this.medicalRecord.id_prof}/prescription-models`, {
          presc_prot_name: name,
          content_type: prescription.content_type ?? 'medication',
          id_presc_type_default: prescription.id_presc_type,
          active: true,
          drugs,
        }).then(() => {
          Alert.toastSuccess('Modelo salvo. Ele já aparece na criação de novas prescrições.');
          this.$emit('onModelSaved');
          bootstrap.Modal.getInstance(document.getElementById('modal_save_prescription_model_' + this.modalRecordId)).toggle();
        });
      }).catch((e) => {
        Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Não foi possível salvar o modelo.');
      }).finally(() => {
        this.savingAsModel = false;
      });
    },
    isMedicationPrescription(prescription) {
      return (prescription?.content_type ?? 'medication') === 'medication';
    },
    importableAttributes(prescription) {
      const typeMap = {
        grid: ['grid'],
        structured_list: ['structured_list'],
        text: ['text', 'html'],
      };
      const types = typeMap[prescription?.content_type] ?? [];
      let attributes = visibleAttributes(this.medicalRecord?.attributes).filter((attr) => types.includes(attr.type));

      if (prescription?.content_type === 'grid') {
        const modelRowKeys = (prescription.prescription_model?.options?.rows ?? []).map((row) => row.key);
        if (modelRowKeys.length > 0) {
          attributes = attributes.filter((attr) => {
            const sourceRowKeys = (attr.options?.rows ?? []).map((row) => row.key);
            return sourceRowKeys.some((key) => modelRowKeys.includes(key));
          });
        }
      }

      return attributes;
    },
    canImportFromRecord(prescription) {
      return !this.isMedicationPrescription(prescription) && this.importableAttributes(prescription).length > 0;
    },
    onModelChange() {
      const model = this.selectedModel;
      this.formStore.presc_name = model?.presc_prot_name ?? '';
      if (!this.isMedicationCreate) {
        this.draftDrugs = [];
        return;
      }
      this.draftDrugs = (model?.drugs ?? []).map((drug) => this.drugFromModel(drug));
    },
    drugFromModel(drug) {
      return {
        item_kind: drug.item_kind ?? 'medication',
        drug_name: drug.drug_name,
        id_drug: drug.id_drug ?? null,
        concentration: drug.concentration ?? '',
        pharmaceutical_form: drug.pharmaceutical_form ?? '',
        quantity: drug.quantity ?? '',
        measure: drug.measure ?? '',
        composition: drug.composition ?? '',
        continuous_use: drug.continuous_use ?? false,
        dosage_desc: drug.dosage_desc ?? '',
        treatment_time: drug.treatment_time ?? '',
        drug_obs: drug.drug_obs ?? '',
        id_drug_admin: drug.id_drug_admin,
        controlled: false,
        regulatory_type: drug.regulatory_type ?? drug.drug?.regulatory_type ?? null,
        _key: `model-${drug.id_presc_prot_drug ?? drug.drug_name}`,
      };
    },
    resetCreateForm() {
      this.fromPrevious = false;
      this.creating = false;
      this.draftDrugs = [];
      this.formStoreErrors = [];
      this.formStore = {
        id_presc_prot: '',
        presc_name: '',
        id_medrec: '',
      };
      this.$refs.drugComposer?.reset?.();
    },
    drugPayload(drug, prescId) {
      return {
        id_presc: prescId,
        item_kind: drug.item_kind ?? 'medication',
        drug_name: drug.drug_name,
        id_drug: drug.id_drug ?? null,
        concentration: drug.concentration || null,
        pharmaceutical_form: drug.pharmaceutical_form || null,
        quantity: drug.quantity || null,
        measure: drug.measure || null,
        composition: drug.composition || null,
        regulatory_type: drug.item_kind === 'compounded' ? (drug.regulatory_type || null) : null,
        continuous_use: drug.continuous_use ?? false,
        dosage_desc: drug.dosage_desc,
        treatment_time: drug.treatment_time || null,
        drug_obs: drug.drug_obs || null,
        id_drug_admin: drug.item_kind === 'compounded' ? null : drug.id_drug_admin,
      };
    },
    focusCreateModal() {
      this.$refs.createModal.focus();
    },
    closeCreateModal() {
      bootstrap.Modal.getInstance(document.getElementById('modal_store_prescription_' + this.modalRecordId))?.hide();
    },
    savePrescriptionPayload(prescription, payload) {
      if (this.readonly || prescription.presc_request_generated) {
        return;
      }
      prescription.presc_payload = payload;
      http.put(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${prescription.id_presc}`, {
        id_medrec: this.medicalRecord.id_medrec,
        presc_payload: payload,
      }).then((response) => {
        prescription.presc_payload = response.data.presc_payload ?? payload;
        if (response.data.prescription_model) {
          prescription.prescription_model = response.data.prescription_model;
        }
      }).catch((e) => {
        Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao salvar prescrição.');
        this.getPrescriptions();
      });
    },
    savePrescriptionObs(prescription, value) {
      if (this.readonly || prescription.presc_request_generated) {
        return;
      }
      http.put(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${prescription.id_presc}`, {
        id_medrec: this.medicalRecord.id_medrec,
        presc_obs: value,
      }).then((response) => {
        prescription.presc_obs = response.data.presc_obs ?? value;
      });
    },
    async importFromRecord(prescription) {
      const candidates = this.importableAttributes(prescription);
      if (!candidates.length) {
        Alert.toastError('Nenhum campo compatível no prontuário desta consulta.');
        return;
      }

      let selectedId = candidates[0].id_medrec_attr;
      if (candidates.length > 1) {
        const inputOptions = {};
        candidates.forEach((attr) => {
          inputOptions[attr.id_medrec_attr] = attr.label;
        });

        const result = await Swal.fire({
          title: 'Importar do prontuário',
          text: 'Selecione o campo da consulta para importar:',
          input: 'select',
          inputOptions,
          showCancelButton: true,
          confirmButtonText: 'Importar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#780f5a',
        });

        if (!result.isConfirmed || !result.value) {
          return;
        }

        selectedId = result.value;
      }

      http.post(
          `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${prescription.id_presc}/import-from-record`,
          {id_medrec_attr: Number(selectedId)},
      ).then((response) => {
        prescription.presc_payload = response.data.presc_payload ?? response.data.prescription?.presc_payload;
        Object.assign(prescription, response.data.prescription);
        Alert.toastSuccess('Dados importados do prontuário.');
        this.getPrescriptions();
      }).catch((e) => {
        Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Não foi possível importar do prontuário.');
      });
    },
    prescriptionBaseUrl(prescription) {
      return `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${prescription.id_presc}`;
    },
    planFor(prescription) {
      return this.regulatoryPlans[prescription.id_presc] ?? null;
    },
    fetchRegulatoryPlan(prescription) {
      if (this.schedule == null || !this.isMedicationPrescription(prescription) || prescription.presc_request_generated) {
        return Promise.resolve(null);
      }

      return http.get(`${this.prescriptionBaseUrl(prescription)}/regulatory-preview`).then((response) => {
        this.regulatoryPlans[prescription.id_presc] = response.data;
        return response.data;
      }).catch(() => {
        return null;
      });
    },
    refreshRegulatoryPlans() {
      this.prescriptions
          .filter((prescription) => this.isMedicationPrescription(prescription) && !prescription.presc_request_generated)
          .forEach((prescription) => this.fetchRegulatoryPlan(prescription));
    },
    headerTypeLabel(prescription) {
      if (prescription.regulatory_type) {
        return regulatoryTypeName(prescription.regulatory_type, '');
      }

      if (prescription.prescription_type?.presc_type_name) {
        return prescription.prescription_type.presc_type_name;
      }

      const documents = this.planFor(prescription)?.documents ?? [];
      if (documents.length === 1) {
        return regulatoryTypeName(documents[0].regulatory_type, documents[0].regulatory_type_description);
      }

      return documents.length > 1 ? `${documents.length} documentos` : '';
    },
    prescriptionTitle(prescription) {
      const typeLabel = prescription.presc_request_generated
          ? this.headerTypeLabel(prescription)
          : 'Prescrição';
      const suffix = [typeLabel, prescription.presc_name].filter(Boolean).join(' - ');

      return suffix ? `#${prescription.id_presc} ${suffix}` : `#${prescription.id_presc}`;
    },
    isPrintBlocked(prescription) {
      if (prescription.presc_request_generated || !this.isMedicationPrescription(prescription)) {
        return false;
      }

      const plan = this.planFor(prescription);

      return plan != null && !plan.can_generate;
    },
    printBlockMessage(prescription) {
      const plan = this.planFor(prescription);
      if (!plan) {
        return 'Imprimir';
      }

      if (plan.blocked?.length) {
        return `Substância de uso proscrito no Brasil: ${plan.blocked.map((item) => item.drug_name).join(', ')}. Remova o item para gerar a prescrição.`;
      }

      if (plan.pending?.length) {
        return plan.pending.length === 1
            ? 'Defina o receituário do medicamento pendente antes de gerar.'
            : `Defina o receituário dos ${plan.pending.length} medicamentos pendentes antes de gerar.`;
      }

      return 'Adicione ao menos um medicamento para gerar a prescrição.';
    },
    /** Só receituário controlado passa pela confirmação da quebra (§7.5). */
    planNeedsConfirmation(plan) {
      return (plan?.documents ?? []).some((documento) => requiresSncrNumber(documento.regulatory_type));
    },
    openCertifierModal() {
      bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_certifier_prescription_' + this.modalRecordId)).toggle();
    },
    async startPrint(prescription, certifier = null) {
      this.selectedPrescription = prescription;

      if (prescription.presc_request_signed) {
        return this.printPrescription(null);
      }

      // Gerou e a assinatura falhou: o PDF já existe, mas ainda precisa do certificado.
      if (prescription.presc_request_generated) {
        return certifier ? this.printPrescription(certifier) : this.openCertifierModal();
      }

      if (!this.isMedicationPrescription(prescription)) {
        return certifier ? this.printPrescription(certifier) : this.openCertifierModal();
      }

      const plan = await this.fetchRegulatoryPlan(prescription);
      if (!plan) {
        Alert.toastError('Não foi possível conferir o receituário desta prescrição. Tente novamente.');
        return;
      }

      if (!plan.can_generate) {
        Alert.toastError(this.printBlockMessage(prescription));
        return;
      }

      // Receituário simples segue o caminho de sempre: não consome numeração,
      // sai num documento só, e a prévia não diria nada que o selo de cada
      // item já não mostre na lista.
      if (!this.planNeedsConfirmation(plan)) {
        return certifier ? this.printPrescription(certifier) : this.openCertifierModal();
      }

      this.planModalPrescription = prescription;
      bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_regulatory_plan_' + this.modalRecordId)).toggle();
    },
    onPlanGenerate(certifier, options = {}) {
      bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_regulatory_plan_' + this.modalRecordId)).toggle();
      this.printPrescription(certifier, options);
    },
    async printPrescription(certifier, options = {}) {
      let prescription = this.selectedPrescription;

      const seguir = await confirmarAssinaturaSemSelo({
        generated: prescription?.presc_request_generated,
        signed: prescription?.presc_request_signed,
        certifier,
      });

      if (!seguir) {
        return;
      }

      let modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_print_prescription_' + this.documentDomId(prescription.id_presc)));
      modal.toggle();

      this.generating = true;
      this.blockedDocuments = [];
      this.messagesGeneratePrescription = {
        message: prescription.presc_request_generated
            ? 'Abrindo os documentos, por favor, aguarde...'
            : 'Gerando prescrição, por favor, aguarde...',
        image: require('@/assets/mp4/8558-floating-document.mp4'),
        showBtnClose: false,
      }

      const certifierId = certifier?.id ?? 'none';

      const taskParams = taskDocumentParams(this.$route, 'prescription', this.medicalRecord, this.taskContext);
      http.post(`${this.prescriptionBaseUrl(prescription)}/print`, {
        ...taskParams,
        certifier_id: certifier?.id,
        // Só quando o médico pediu explicitamente: o padrão continua bloqueando
        // o documento controlado sem numeração.
        allow_without_numbering: options.allowWithoutNumbering === true,
      }).then(async (response) => {
        await new Promise(r => setTimeout(r, 1000));

        const documents = response.data.documents ?? [];
        if (documents.length) taskDocumentLinked(taskParams);
        this.blockedDocuments = response.data.blocked ?? [];

        (response.data.unnumbered ?? []).forEach((item) => {
          Alert.toastWarning(`${item.regulatory_type_description} emitida sem numeração do SNCR.`);
        });

        if (documents.length === 0) {
          this.messagesGeneratePrescription = {
            message: 'Nenhum documento foi gerado para esta prescrição.',
            image: require('@/assets/mp4/4386-connection-error.mp4'),
            showBtnClose: true,
          }
          return;
        }

        if (certifierId !== 'vidaas') {
          this.getPrescriptions();

          this.messagesGeneratePrescription = {
            message: documents.length === 1 ? 'Baixando Prescrição ...' : `Baixando ${documents.length} documentos ...`,
            image: require('@/assets/mp4/97795-download-green.mp4'),
            showBtnClose: false,
          }
        }

        await new Promise(r => setTimeout(r, 600));

        const pendingSignature = documents.filter((document) => !document.prescription.presc_request_signed);

        if (pendingSignature.length === 0 || certifierId === 'none') {
          return this.showGeneratedDocuments(documents, modal);
        }

        if (certifierId === 'vidaas') {
          return await this.signCloudDocuments(pendingSignature, certifier, modal);
        }

        if (certifierId === 'token') {
          return await this.signTokenDocuments(pendingSignature, modal);
        }

        this.showGeneratedDocuments(documents, modal);
      }).catch((e) => {
        this.showGenerationError(e);
      }).finally(() => {
        this.generating = false;
      });
    },
    showGenerationError(e) {
      const {code, message} = sncrError(e);
      const precisaAutenticar = ['SNCR_SESSION_EXPIRED', 'SNCR_POOL_EXHAUSTED', 'SNCR_NO_NUMBERS_AVAILABLE'].includes(code);

      this.messagesGeneratePrescription = {
        message: message ?? 'Não foi possível gerar a prescrição.',
        image: require('@/assets/mp4/4386-connection-error.mp4'),
        showBtnClose: true,
        actionLabel: precisaAutenticar && this.sncrProfessionalId ? 'Autenticar no gov.br' : null,
        action: () => this.authenticateOnGovBr(),
      }
    },
    authenticateOnGovBr() {
      if (!this.sncrProfessionalId) {
        return;
      }

      startGovBrAuth(this.sncrProfessionalId, this.sncrReturnTo).catch(() => {
        Alert.toastError('Não foi possível abrir a autenticação do gov.br. Tente novamente.');
      });
    },
    signingMessage(index, total, extra) {
      const header = total === 1
          ? 'Assinando digitalmente...'
          : `Assinando digitalmente o documento ${index + 1} de ${total}...`;

      return extra ? `${header}<br><br>${extra}` : header;
    },
    showGeneratedDocuments(documents, modal) {
      if (documents.length && !this.blockedDocuments.length && documents.every(item => item.prescription.presc_request_signed)) this.$emit('signed');
      this.getPrescriptions();

      if (documents.length === 1 && this.blockedDocuments.length === 0) {
        const document = documents[0];
        return this.showPdfViewer(
            `data:application/pdf;base64,${document.file_base64}`,
            document.authenticator.file.file_name,
            document.prescription.presc_request_signed,
            modal,
        );
      }

      this.generatedDocuments = documents;
      modal.toggle();
      bootstrap.Modal.getOrCreateInstance(window.document.getElementById('modal_prescription_documents_' + this.modalRecordId)).toggle();
    },
    openGeneratedDocument(document) {
      this.showPdfViewer(
          `data:application/pdf;base64,${document.file_base64}`,
          document.authenticator.file.file_name,
          document.prescription.presc_request_signed,
          null,
          false,
      );
    },
    async signCloudDocuments(documents, certifier, modal) {
      const signed = [];
      const vidaasNotice = 'Para concluir, caso seja solicitado, autorize a transação no aplicativo em seu celular.<br>' +
          'A liberação do seu celular é válida por 24 horas. Dentro desse período, você não precisará repetir esta etapa.';

      for (let index = 0; index < documents.length; index++) {
        const document = documents[index];

        this.messagesGeneratePrescription = {
          message: this.signingMessage(index, documents.length, vidaasNotice),
          image: require('@/assets/mp4/signing.mp4'),
          showBtnClose: false,
          loop: true,
        };

        try {
          const response = await http.post(`${certifier.url}/prescription/${document.prescription.id_presc}`);
          signed.push(this.asSignedDocument(document, response.data.base64_signed));
        } catch (e) {
          Alert.toastError('Erro ao assinar documento');
          return this.showGeneratedDocuments([...signed, ...documents.slice(signed.length)], modal);
        }
      }

      this.showGeneratedDocuments(signed, modal);
    },
    asSignedDocument(document, base64Signed) {
      return {
        ...document,
        file_base64: base64Signed,
        prescription: {...document.prescription, presc_request_signed: true},
      };
    },
    async askCertificatePassword() {
      const stored = sessionStorage.getItem('certificate_password');
      if (stored) {
        return stored;
      }

      const { value: password } = await Swal.fire({
        title: 'Senha do certificado',
        input: 'text',
        inputLabel: 'Informe a senha',
        confirmButtonText: 'Assinar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#780f5a',
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return 'Informe a senha do certificado digital!'
          }
        }
      });

      return password ? Base64.encode(password) : null;
    },
    selectCertificateAlias() {
      return new Promise((resolve, reject) => {
        let wsCertificates = new WebSocket(Config.protocol_ws + '://localhost:8044/certificates');

        wsCertificates.onopen = () => {
          wsCertificates.send('1');
        }

        wsCertificates.onmessage = async (event) => {
          let certificatesRaw = JSON.parse(event.data);
          wsCertificates.close(1000);

          if (certificatesRaw.length <= 0) {
            Alert.toastError('Nenhum certificado encontrado.');
            this.messagesGeneratePrescription.showBtnClose = true;
            return resolve(null);
          }

          let certificates = certificatesRaw.sort((certificateOne, certificateTwo) => {
            let expiryDateOne = new Date(certificateOne.expiryDate.toUpperCase().replace("BRT", "GMT-0300"));
            let expiryDateTwo = new Date(certificateTwo.expiryDate.toUpperCase().replace("BRT", "GMT-0300"));

            return expiryDateTwo - expiryDateOne;
          });

          if (certificates.length === 1) {
            return resolve(certificates[0].alias);
          }

          let inputOptions = {};
          certificates.forEach((certificate) => {
            inputOptions[certificate.alias] = certificate.alias;
          });

          const { value: aliasSelected } = await Swal.fire({
            title: 'Certificados instalados',
            input: 'select',
            inputOptions,
            inputPlaceholder: 'Selecione um certificado',
            showCancelButton: true,
            confirmButtonColor: '#780f5a',
            inputValidator: (value) => {
              if (!value) {
                return 'Selecione um certificado.';
              }
            }
          });

          resolve(aliasSelected ?? null);
        }

        wsCertificates.onerror = () => {
          this.messagesGeneratePrescription.showBtnClose = true;
          Swal.fire({
            title: "Opsss",
            text: "Não conseguimos identificar o Assinador Ubbimed instalado.",
            icon: "warning",
            confirmButtonText: 'OK',
            confirmButtonColor: '#780f5a',
          }).then(() => {
            window.open('/assinador/download')
          });

          reject(new Error('Assinador Ubbimed indisponível.'));
        }
      });
    },
    async signTokenDocuments(documents, modal) {
      if (!window.WebSocket) {
        Alert.toastError('Seu navegador não tem suporte a conexão websocket.');
        return this.showGeneratedDocuments(documents, modal);
      }

      const hadStoredPassword = sessionStorage.getItem('certificate_password') != null;

      if (!hadStoredPassword) {
        modal.toggle();
      }

      const certificatePassword = await this.askCertificatePassword();

      if (!hadStoredPassword) {
        modal.toggle();
      }

      if (!certificatePassword) {
        return this.showGeneratedDocuments(documents, modal);
      }

      this.messagesGeneratePrescription = {
        message: this.signingMessage(0, documents.length),
        image: require('@/assets/mp4/signing.mp4'),
        showBtnClose: false,
        loop: true,
      };

      let certificateAlias = null;

      if (OS.getOS() === 'Windows') {
        try {
          certificateAlias = await this.selectCertificateAlias();
        } catch (e) {
          return;
        }

        if (!certificateAlias) {
          return this.showGeneratedDocuments(documents, modal);
        }
      }

      const signed = [];

      for (let index = 0; index < documents.length; index++) {
        this.messagesGeneratePrescription = {
          message: this.signingMessage(index, documents.length),
          image: require('@/assets/mp4/signing.mp4'),
          showBtnClose: false,
          loop: true,
        };

        try {
          signed.push(await this.signDocumentWithToken(documents[index], certificatePassword, certificateAlias));
        } catch (e) {
          return;
        }
      }

      Alert.toastSuccess('Assinatura realizada com sucesso.');
      this.showGeneratedDocuments(signed, modal);

      if (!hadStoredPassword) {
        Swal.fire({
          text: 'Quer salvar a senha para nas próximas assinaturas?',
          icon: 'success',
          showCloseButton: true,
          showCancelButton: true,
          confirmButtonText: 'Sim, salvar senha',
        }).then((result) => {
          if (result.isConfirmed) {
            sessionStorage.setItem('certificate_password', certificatePassword);
          }
        });
      }
    },
    signDocumentWithToken(document, certificatePassword, certificateAlias) {
      return new Promise((resolve, reject) => {
        let connectedPdfSign = false;
        let wsSignConnection = new WebSocket(Config.protocol_ws + '://localhost:8044/pdf/sign');

        wsSignConnection.onopen = () => {
          connectedPdfSign = true;
          let dataToBeSign = {
            certificate_pfx_as_base64: null,
            certificate_password: Base64.decode(certificatePassword),
            reason: 'Assinatua de documentos Ubbimed.',
            location: this.place.place_name,
            contact: `${this.userAuth.user_name} <${this.userAuth.user_email}>`,
            certificate_alias: certificateAlias,
            file_id: document.prescription.id_presc,
            file_as_base64: document.file_base64
          };

          wsSignConnection.send(JSON.stringify(dataToBeSign));
        }

        wsSignConnection.onmessage = (message) => {
          let responseSignature = JSON.parse(message.data);
          const signedBase64 = responseSignature.file_signed_as_base64;
          const file = Base64.str2Blob(signedBase64)
          const formData = new FormData();
          formData.append('file', file, 'file.pdf')

          let url = `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${document.prescription.id_presc}/upload-signed`;
          http.post(url, formData).then(() => {
            resolve(this.asSignedDocument(document, signedBase64));
          }).catch((e) => {
            this.messagesGeneratePrescription = {
              message: 'Não foi possível salvar o documento assinado.',
              image: require('@/assets/mp4/4386-connection-error.mp4'),
              showBtnClose: true,
            }
            reject(e);
          }).finally(() => {
            wsSignConnection.close(1000);
          });
        }

        wsSignConnection.onerror = (error) => {
          Swal.close();
          this.messagesGeneratePrescription = {
            message: connectedPdfSign
                ? 'Ocorreu um erro inesperado com o assinador Ubbimed. (' + error.code + ')'
                : 'Não foi possível estabelecer uma conexão com o assinador. ' +
                  '<br><br>Verifique se o assinador Ubbimed está instalado ou se o mesmo está em execução na sua máquina.',
            image: require('@/assets/mp4/4386-connection-error.mp4'),
            showBtnClose: true,
            loop: true,
          }

          reject(error);
        }

        wsSignConnection.onclose = (event) => {
          if (event.code === 1006) {
            this.messagesGeneratePrescription = {
              message: 'Falha na comunição com o assinador.',
              image: require('@/assets/mp4/4386-connection-error.mp4'),
              showBtnClose: true,
              loop: true,
            }

            reject(event);
          } else if (event.code === 1011) {
            this.messagesGeneratePrescription = {
              message: event.reason,
              image: require('@/assets/mp4/4386-connection-error.mp4'),
              showBtnClose: true,
              loop: true,
            }

            reject(event);
          }
        }
      });
    },
    getPrescriptions() {
      if (this.schedule == null)
        return;

      return http.get(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions`).then((response) => {
        this.prescriptions = this.taskContext ? response.data.filter(item => Number(item.id_task_origin) === Number(this.taskContext.id)) : response.data;
        this.refreshRegulatoryPlans();
      });
    },
    submitFormStore() {
      if (this.fromPrevious || this.creating) return;

      if (this.isMedicationCreate) {
        const leftover = this.$refs.drugComposer?.commitDraft?.() ?? 'empty';
        if (leftover === 'invalid') {
          Alert.toastError('Complete o medicamento em edição antes de cadastrar.');
          return;
        }
      }

      this.formStore.id_medrec = this.medicalRecord.id_medrec;
      this.formStoreErrors = [];
      this.creating = true;

      // Modelo de medicamento vira lista editável no modal: cria a receita
      // pelo tipo escolhido e grava os itens em seguida. Modelo de grade/
      // lista/texto continua copiado pela API.
      const payload = {
        ...taskDocumentParams(this.$route, 'prescription', this.medicalRecord, this.taskContext),
        id_medrec: this.medicalRecord.id_medrec,
        presc_name: this.formStore.presc_name,
      };
      if (!this.isMedicationCreate && this.formStore.id_presc_prot) {
        payload.id_presc_prot = this.formStore.id_presc_prot;
      }

      const url = `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions`;
      http.post(url, payload).then((response) => {
        const prescription = response.data;
        if (!this.isMedicationCreate || !prescription?.id_presc || this.draftDrugs.length === 0) {
          return Promise.resolve(prescription);
        }

        return this.draftDrugs.reduce((chain, drug) => chain.then(() => {
          return http.post(`${url}/${prescription.id_presc}/drugs`, this.drugPayload(drug, prescription.id_presc));
        }), Promise.resolve()).then(() => prescription);
      }).then(() => {
        this.getPrescriptions();
        Alert.toastSuccess(this.draftDrugs.length
            ? 'Prescrição cadastrada com os medicamentos.'
            : 'Prescrição cadastrada com sucesso.');
        this.resetCreateForm();
        this.closeCreateModal();
      }).catch((e) => {
        this.getPrescriptions();
        Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Não foi possível cadastrar a prescrição.');
        if (e.response?.data?.errors) {
          this.formStoreErrors = e.response.data.errors;
        }
      }).finally(() => {
        this.creating = false;
      });
    },
    deletePrescription(prescId) {
      Swal.fire({
        title: 'Heeyy',
        text: "Tem certeza que deseja remover a prescrição?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: 'btn btn-secundary'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          http.delete(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${prescId}`).then(_ => {
            this.getPrescriptions();
            Alert.toastSuccess("Prescrição excluÍda com sucesso.");
          }).catch(e => {
            Alert.toastError(e.response.data);
          })
        }
      });
    },
    reopenPrescription(prescId) {
      Swal.fire({
        title: 'Heeyy',
        text: "A reabertura cancela os PDFs desta geração, inclusive os de outro tipo de receituário, e abre uma prescrição nova para edição. Tem certeza que deseja reabrir?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, reabrir!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: 'btn btn-secundary'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          http.post(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.medicalRecord.id_medrec}/prescriptions/${prescId}/reopen`).then(_ => {
            this.getPrescriptions();
            Alert.toastSuccess("Prescrição reaberta com sucesso.");
          }).catch(e => {
            Alert.toastError(e.response.data);
          })
        }
      });
    },
    showPdfViewer(pdfSource, fileName, signed, modalOri, fecharOrigem = true) {
      this.loadDocumentsDelivery();

      this.fileName = fileName;
      this.pdfSource = pdfSource;
      this.signed = signed;

      if (fecharOrigem) {
        modalOri?.hide();
      }

      bootstrap.Modal.getOrCreateInstance(
          document.getElementById('modal_pdf_viewer_prescription_' + this.modalRecordId)
      ).show();
    },
  }

}
</script>

<style scoped src="../shared/prescriptionCreate.css"></style>

<style>
/* Não escopado de propósito: o visualizador precisa ficar acima do modal de
   documentos que o abriu.

   O concorrente é o `.modal { z-index: 1055 !important }` escopado abaixo, que
   o Vue também aplica na raiz dos componentes filhos — ou seja, nos dois
   modais. Como `.modal[data-v-x]` tem a mesma especificidade de
   `.modal[id^=...]`, um seletor com dois atributos empataria e perderia por vir
   antes no arquivo; daí o `.fade` para desempatar pela especificidade. */
.modal.fade[id^="modal_pdf_viewer_prescription_"] {
  z-index: 1090 !important;
}
</style>

<style scoped>

.action-btn-print:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Card principal de prescrição */
.prescription-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #780f5a;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  transition: border-color 0.2s ease;
}

.prescription-card:hover {
  border-color: #dcdcdc;
  border-left-color: #780f5a;
}

/* Header da prescrição */
.prescription-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
}

.prescription-header:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  border-bottom-color: #6a0d4e;
}

.prescription-title {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.prescription-icon {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prescription-icon .svg-icon {
  color: #6c757d;
}

.prescription-info {
  flex: 1;
}

.prescription-name {
  color: #495057;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.prescription-status {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-icon {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.status-icon:hover {
  background: #e9ecef;
  transform: scale(1.05);
}

.status-icon.locked {
  background: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.status-icon.unlocked {
  background: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.status-icon.pdf {
  background: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.status-icon.signed {
  background: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.prescription-toolbar {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 10px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
}

.prescription-toolbar:hover {
  background: #e9ecef;
  border-color: #780f5a;
  transform: scale(1.05);
}

.prescription-toolbar .svg-icon {
  color: #6c757d;
  font-size: 1.2rem;
}

.prescription-toolbar:hover .svg-icon {
  color: #780f5a;
}

/* Toolbar principal */
.card-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px 20px 0;
  border-radius: 12px 12px 0 0;
}

.main-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.main-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #ffffff;
  color: #666666;
}

.main-btn:hover {
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.15);
}

.main-btn-primary {
  background: #780f5a;
  color: white;
  border-color: #780f5a;
}

.main-btn-primary:hover {
  background: #6a0d4e;
  border-color: #6a0d4e;
  color: white;
}

.main-btn-success {
  background: #50cd89;
  color: white;
  border-color: #50cd89;
}

.main-btn-success:hover {
  background: #47be7d;
  border-color: #47be7d;
  color: white;
  box-shadow: 0 2px 8px rgba(80, 205, 137, 0.25);
}

.main-btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.main-btn-secondary:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.main-btn-info {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.main-btn-info:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

/* Ações da prescrição */
.prescription-actions {
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Telas estreitas: em linha os botoes quebravam em larguras desiguais, com o
   ultimo sozinho. Em grade eles ficam do mesmo tamanho e alinhados. */
@media (max-width: 767.98px) {
  .action-buttons {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .action-btn {
    justify-content: center;
    text-align: center;
    padding: 10px 12px;
    line-height: 1.25;
  }
}

/* So empilha em uma coluna quando duas nao cabem mais: a 414px quatro botoes
   em coluna gastam meia tela de altura. */
@media (max-width: 359.98px) {
  .action-buttons {
    grid-template-columns: 1fr;
  }
}


.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #ffffff;
  color: #666666;
}

.action-btn:hover {
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 6px rgba(120, 15, 90, 0.15);
}

.action-btn-primary {
  background: #780f5a;
  color: white;
  border-color: #780f5a;
}

.action-btn-primary:hover {
  background: #6a0d4e;
  border-color: #6a0d4e;
  color: white;
}

.action-btn-success {
  background: #50cd89;
  color: white;
  border-color: #50cd89;
}

.action-btn-success:hover {
  background: #47be7d;
  border-color: #47be7d;
  color: white;
  box-shadow: 0 2px 6px rgba(80, 205, 137, 0.25);
}

.action-btn-warning {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.action-btn-warning:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.action-btn-info {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.action-btn-info:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.action-btn-danger {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.action-btn-danger:hover {
  background: #e9ecef;
  border-color: #dc3545;
  color: #dc3545;
}

/* Botão de impressão destacado */
.action-btn-print {
  color: #780f5a;
  border: 1px solid #780f5a;
}

.action-btn-print:hover {
  color: #6a0d4e;
  border-color: #6a0d4e;
}

/* Botão de exclusão destacado */
.action-btn-delete {
  color: #dc3545;
  border: 1px solid #dc3545;
}

.action-btn-delete:hover {
  color: #c82333;
  border-color: #c82333;
}

/* Botão de reabertura destacado */
.action-btn-reopen {
  color: #0d6efd;
  border: 1px solid #0d6efd;
}

.action-btn-reopen:hover {
  color: #0b5ed7;
  border-color: #0b5ed7;
}

/* Chat messages */
.chat-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.chat-message .badge.align-self-end {
  align-self: flex-end;
}

.chat-message .badge {
  max-width: 80%;
  word-break: break-word;
  white-space: pre-line;
  display: inline-block;
}

.modal {
  z-index: 1055 !important;
}

.modal-backdrop {
  z-index: 1050 !important;
}

/* Animações */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.prescription-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Estado vazio */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  margin: 20px 0;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-state-icon {
  margin-bottom: 24px;
  opacity: 0.6;
  animation: float 3s ease-in-out infinite;
}

.empty-state-title {
  color: #780f5a;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 16px 0;
  text-shadow: 0 2px 4px rgba(120, 15, 90, 0.1);
}

.empty-state-description {
  color: #6c757d;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  font-weight: 400;
}

/* Animação flutuante para o ícone */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

</style>
